import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/backendUrl';

/**
 * Tiny server-status pill for the Login page.
 *
 * Pings `/api/health` (public, unauthenticated) on mount and every 20s.
 * Renders a colored dot + concise label:
 *   🟢 ok          → backend + DB reachable
 *   🟠 degraded    → backend up, DB ping failed → login will succeed but app
 *                    pages may misbehave
 *   🔴 down        → backend unreachable or 5xx → logins WILL fail
 *
 * Click the pill to force a recheck without waiting for the next interval.
 */
const STATUS_STYLES = {
  ok:       { dot: 'bg-emerald-500', txt: 'text-emerald-700', label: 'Server online' },
  degraded: { dot: 'bg-amber-500',   txt: 'text-amber-700',   label: 'Server degraded' },
  down:     { dot: 'bg-rose-500',    txt: 'text-rose-700',    label: 'Server unreachable' },
  checking: { dot: 'bg-slate-400 animate-pulse', txt: 'text-slate-500', label: 'Checking…' },
};

const ServerStatus = () => {
  const [state, setState] = useState('checking');
  const [latency, setLatency] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);

  const probe = async () => {
    setState((prev) => (prev === 'checking' ? prev : 'checking'));
    const started = performance.now();
    try {
      const r = await axios.get(`${BACKEND_URL}/api/health`, { timeout: 5000 });
      const elapsed = Math.round(performance.now() - started);
      setLatency(elapsed);
      setLastChecked(new Date());
      if (r.data?.status === 'ok') setState('ok');
      else setState('degraded');
    } catch (err) {
      setLastChecked(new Date());
      setLatency(null);
      setState('down');
    }
  };

  useEffect(() => {
    probe();
    const t = setInterval(probe, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const s = STATUS_STYLES[state] || STATUS_STYLES.checking;
  const title = lastChecked
    ? `Last checked ${lastChecked.toLocaleTimeString()}${latency != null ? ` · ${latency}ms` : ''} — click to recheck`
    : 'Click to check now';

  return (
    <button
      type="button"
      onClick={probe}
      title={title}
      data-testid="login-server-status"
      className={`inline-flex items-center gap-2 text-xs ${s.txt} hover:opacity-80`}
    >
      <span className={`inline-block w-2 h-2 rounded-full ${s.dot}`} />
      <span className="font-medium">{s.label}</span>
      {state === 'ok' && latency != null && (
        <span className="text-slate-400">· {latency}ms</span>
      )}
    </button>
  );
};

export default ServerStatus;
